import {
  Box,
  Heading,
  Stack,
  Icon,
  IconButton,
  useDisclosure,
  useMediaQuery,
  Spinner,
  MenuButton,
  MenuList,
  MenuItem,
  Menu,
  Tooltip,
} from '@chakra-ui/react';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import React, { useEffect, useMemo, useState, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { IconCaretRight, IconExport, IconFilter, IconSort } from 'assets/icons';
import Loader from 'components/Loader';
import SearchField from 'components/SearchField';
import { getBounds } from 'containers/WhoIsWorking/shared/helpers';
import { useDownloadWhoIsWorking } from 'hooks/whoIsWorking/useDownloadWhoIsWorking';
import { useFetchGeolocationsByTimesheetId } from 'hooks/whoIsWorking/useFetchGeolocationsBytimesheetId';
import { IWhoIsWorkingSearchQuery, useFetchWhoIsWorking } from 'hooks/whoIsWorking/useFetchWhoIsWorking';

import { useSearchParamStore } from '../shared/useSearchParamStore';
import FilterForm from './FilterForm/FilterForm';
import { initialFilterFormData } from './FilterForm/schema';
import WorkingEmployeeCard from './WorkingEmployeeCard';

interface IWorkingEmployeesProps {
  activeEmployee: any;
  setActiveEmployee: (data: any) => void;
  mapRef?: any;
}

const WorkingEmployees: React.FC<IWorkingEmployeesProps> = ({ setActiveEmployee, activeEmployee, mapRef }) => {
  const { t } = useTranslation(['whoIsWorking', 'common']);

  const [timesheetId, setTimesheetId] = useState<any>(null);
  const [employeesData, setEmployeesData] = useState<any>([]);
  const [filteredEmployeesData, setFilteredEmployeesData] = useState<any>([]);

  const [isLargerThan768, isLargerThan548] = useMediaQuery(['(min-width: 768px)', '(min-width: 548px)'], {
    ssr: false,
  });
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  const { working, group_ids, updateSearchQuery } = useSearchParamStore();
  const { data, isFetching } = useFetchWhoIsWorking({ working, group_ids }, { enabled: false });
  const { data: geolocationsData, isFetching: isGeoLocationFetching } = useFetchGeolocationsByTimesheetId(timesheetId);
  const { mutateAsync: mutateAsyncDownloadCSV, isLoading: isDownloadCSVLoading } = useDownloadWhoIsWorking();

  const sortOptions = useMemo(
    () => [
      { label: t('common:firstName'), value: 'firstName' },
      { label: t('common:role'), value: 'roleId' },
    ],
    [t]
  );

  useEffect(() => {
    const whoIsWorkingMascotEl = document.getElementById('who-is-working-mascot');

    if (!whoIsWorkingMascotEl) return;

    if (isLargerThan768) {
      whoIsWorkingMascotEl.style.paddingRight = isOpen ? '462px' : '0px';
    } else if (isLargerThan548) {
      whoIsWorkingMascotEl.style.paddingRight = isOpen ? '312px' : '0px';
    } else {
      whoIsWorkingMascotEl.style.paddingRight = '0px';
    }
  }, [isOpen, isLargerThan548, isLargerThan768, isFetching]);

  useEffect(() => {
    if (Array.isArray(data?.payload)) {
      setEmployeesData(data?.payload || []);
      setFilteredEmployeesData(data?.payload || []);
    }
  }, [data]);

  useEffect(() => {
    const item: any = geolocationsData?.payload;

    if (item) {
      setActiveEmployee(item);
      const allCoordinates = [...(item?.geo_locations || [])];

      if (item.start_latitude && item.start_longitude) {
        allCoordinates.unshift({ latitude: item.start_latitude, longitude: item.start_longitude });
      }

      if (item.end_latitude && item.end_longitude) {
        allCoordinates.push({ latitude: item.end_latitude, longitude: item.end_longitude });
      }

      if (!allCoordinates.length) return;

      const coordinatesBounds = getBounds(allCoordinates);

      const { longitude, latitude, zoom } = new WebMercatorViewport({
        width: window.innerWidth,
        height: Math.max(600, window.innerHeight),
      }).fitBounds(coordinatesBounds, {
        padding: { left: 150, top: 150, right: 150, bottom: 100 },
        offset: [0, -100],
      });

      mapRef.current?.flyTo({ center: [longitude, latitude], duration: 2500, zoom: zoom });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geolocationsData]);

  const handleLocationClick = useCallback((item: any) => {
    setTimesheetId(item?.timesheet_id);
  }, []);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim();

    const updatedEmployeesData = employeesData.filter((item: any) =>
      (item.first_name.toLowerCase() + ' ' + item.last_name.toLowerCase()).includes(inputValue.toLowerCase())
    );

    setFilteredEmployeesData(updatedEmployeesData);
  };

  const handleSortMenuChange = (selectedSort: ILabelValueOptions) => {
    if (selectedSort?.value === 'roleId') {
      // Remember to not mutate the data only but pass a new reference to the data using .slice() or similar methods so that react will know that a new value has been updated
      const updatedEmployeesData = employeesData.slice().sort((item1: any, item2: any) => item1.roleId - item2.roleId);
      setEmployeesData(updatedEmployeesData);
      setFilteredEmployeesData(updatedEmployeesData);
    } else if (selectedSort?.value === 'firstName') {
      const updatedEmployeesData = employeesData.slice(0);
      updatedEmployeesData.sort((item1: any, item2: any) =>
        item1.first_name.toLowerCase() > item2.first_name.toLowerCase()
          ? 1
          : item2.first_name.toLowerCase() > item1.first_name.toLowerCase()
            ? -1
            : 0
      );

      setEmployeesData(updatedEmployeesData);
      setFilteredEmployeesData(updatedEmployeesData);
    } else {
      setEmployeesData(data?.payload);
      setFilteredEmployeesData(data?.payload);
    }
  };

  const handleFilterFormSubmit = (values: typeof initialFilterFormData, onClose: () => void) => {
    const groupIds = values.group_ids?.length ? values.group_ids.map((group: any) => group.id) : [];

    const searchQuery: IWhoIsWorkingSearchQuery = {
      working: values.notWorking ? 'false' : 'true',
      group_ids: groupIds.join(','),
    };
    setTimesheetId(null);
    setActiveEmployee(null);
    updateSearchQuery(searchQuery);
    if (onClose) {
      onClose();
    }
  };

  const handleDownloadCSV = async () => {
    try {
      await mutateAsyncDownloadCSV();
    } catch (error) {
      console.error('CSV Download Error:', error);
    }
  };

  return (
    <Box
      zIndex={1}
      position="absolute"
      width={isLargerThan768 ? '450px' : '300px'}
      height="100%"
      bg="white"
      right="0"
      top="0"
      bottom="0"
      p="4"
      pb="6"
      transform={isOpen ? 'translateX(0)' : 'translateX(100%)'}
      transition="all 0.2s"
    >
      <IconButton
        aria-label="toggle working user"
        icon={<Icon as={IconCaretRight} p="0" />}
        size="sm"
        height={{ base: '45px', md: '60px' }}
        width={{ base: '10px', md: '30px' }}
        px="1"
        bg="white"
        position="absolute"
        top="6"
        right="100%"
        _hover={{
          bg: 'white',
        }}
        _active={{
          bg: 'white',
        }}
        boxShadow="lg"
        onClick={onToggle}
        data-testid="whoisworking-sidebar-toggle"
      />
      <Box display="flex" alignItems="center" justifyContent="space-between" mb="5">
        <Heading as="h3" size="md" data-testid="whoisworking-sidebar-title">
          {filteredEmployeesData.length}{' '}
          {working === 'true' ? t('whoIsWorking:usersWorking') : t('whoIsWorking:usersNotWorking')}
        </Heading>
        {isGeoLocationFetching && <Spinner emptyColor="gray.200" color="primary.500" thickness="3px" />}
      </Box>
      <Stack direction={{ base: 'column', md: 'row' }} position="relative" spacing="2">
        <SearchField
          inputGroupProps={{ size: 'lg' }}
          inputProps={{ placeholder: t('whoIsWorking:searchForAUser'), onChange: handleSearchInputChange }}
        />
        <Box flex={{ md: '1 0 90px' }} display="flex" alignItems="center" justifyContent="flex-end">
          <Menu placement="bottom-end" isLazy={true}>
            <Tooltip label="Sort by" hasArrow placement="top">
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<Icon as={IconSort} />}
                variant="outline"
                mr="3"
                data-testid="sort-button"
              />
            </Tooltip>
            <MenuList minWidth="200px">
              <Heading as="h5" size="sm" px="3" mb="3">
                {t('common:sortBy')}
              </Heading>
              {sortOptions.map(({ label, value }) => {
                return (
                  <MenuItem key={value} onClick={() => handleSortMenuChange({ label, value })}>
                    {label}
                  </MenuItem>
                );
              })}
            </MenuList>
          </Menu>

          <Menu>
            {({ onClose }) => (
              <>
                <Tooltip label="Filters" hasArrow placement="top">
                  <MenuButton
                    as={IconButton}
                    aria-label="Options"
                    icon={<Icon as={IconFilter} />}
                    variant="outline"
                    data-testid="filter-button"
                  />
                </Tooltip>
                <MenuList minWidth="200px">
                  <FilterForm handleFilterFormSubmit={values => handleFilterFormSubmit(values, onClose)} />
                </MenuList>
              </>
            )}
          </Menu>
        </Box>
        <Box>
          <Tooltip label="Download CSV" hasArrow bg="black">
            <IconButton
              aria-label="Download CSV"
              icon={<Icon as={IconExport} color="primary.600" />}
              variant="outline"
              size="md"
              m="1"
              onClick={() => handleDownloadCSV()}
              isLoading={isDownloadCSVLoading}
            />
          </Tooltip>
        </Box>
      </Stack>
      <Stack
        direction="column"
        mt="5"
        height={{ base: 'calc(100% - 150px)', md: 'calc(100% - 100px)' }}
        overflowY="auto"
        pr="1"
      >
        {isFetching ? <Loader /> : null}
        {filteredEmployeesData.length && !isFetching
          ? filteredEmployeesData.map((item: any) => {
              return (
                <WorkingEmployeeCard
                  key={item.user_id}
                  item={item}
                  activeEmployee={activeEmployee}
                  handleLocationClick={handleLocationClick}
                />
              );
            })
          : null}
      </Stack>
    </Box>
  );
};

export default memo(WorkingEmployees);
